import React from "react";
import PropTypes from "prop-types";
import {
  Briefcase,
  Save,
  Users,
  Loader2,
  Info,
  AlertCircle,
} from "lucide-react";
import DefaultEditor from "react-simple-wysiwyg";
import Modal from "@/components/ui/modal";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";

const LaborEntriesModal = ({
  show,
  onHide,
  selectedRepairJob,
  newLaborEntry,
  setNewLaborEntry,
  handleLaborEntryChange,
  isTechniciansLoading,
  technicians,
  currencySymbol,
  handleLaborEntrySave,
  addLaborMutation,
  handleViewLabor,
  repairJobs,
}) => {
  const startDate = newLaborEntry.start_time
    ? new Date(newLaborEntry.start_time)
    : null;
  const endDate = newLaborEntry.end_time
    ? new Date(newLaborEntry.end_time)
    : null;

  const hasValidStart = Boolean(startDate && !isNaN(startDate.getTime()));
  const hasValidEnd = Boolean(endDate && !isNaN(endDate.getTime()));

  const isEndBeforeStart = Boolean(
    hasValidStart && hasValidEnd && endDate <= startDate,
  );
  const isEndTimeWithoutStart = Boolean(
    newLaborEntry.end_time && !newLaborEntry.start_time,
  );
  const isStartInvalid = Boolean(
    newLaborEntry.start_time && !hasValidStart,
  );
  const isEndInvalid = Boolean(
    newLaborEntry.end_time && !hasValidEnd,
  );

  const calculateHours = () => {
    if (!hasValidStart || !hasValidEnd || isEndBeforeStart) return "0.00";
    const diffMs = endDate - startDate;
    const diffHours = diffMs / (1000 * 60 * 60);
    return diffHours > 0 ? diffHours.toFixed(2) : "0.00";
  };

  const calculateTotalCost = () => {
    if (!hasValidStart || !hasValidEnd || isEndBeforeStart) return "0.00";
    const diffMs = endDate - startDate;
    const diffHours = diffMs / (1000 * 60 * 60);
    const hours = diffHours > 0 ? diffHours : 0;
    const cost = hours * parseFloat(newLaborEntry.hourly_rate || 0);
    return cost.toFixed(2);
  };

  const handleValidateAndSave = () => {
    if (!newLaborEntry.technician_id) {
      toast.error("Please select a technician");
      return;
    }
    if (
      newLaborEntry.hourly_rate === undefined ||
      newLaborEntry.hourly_rate === "" ||
      parseFloat(newLaborEntry.hourly_rate) < 0
    ) {
      toast.error("Please enter a valid hourly rate");
      return;
    }
    if (!newLaborEntry.start_time) {
      toast.error("Start time is required");
      return;
    }
    if (!hasValidStart) {
      toast.error("Invalid start time format");
      return;
    }
    if (newLaborEntry.end_time) {
      if (!hasValidEnd) {
        toast.error("Invalid end time format");
        return;
      }
      if (endDate <= startDate) {
        toast.error("End time must be after start time");
        return;
      }
    }
    handleLaborEntrySave();
  };

  const isSaveDisabled =
    Boolean(addLaborMutation?.isPending) ||
    !newLaborEntry.technician_id ||
    !newLaborEntry.start_time ||
    !hasValidStart ||
    isEndBeforeStart ||
    isEndTimeWithoutStart;

  return (
    <Modal
      open={show}
      onOpenChange={(v) => !v && onHide()}
      title={
        <div className="flex items-center gap-2">
          <span>Add Labor Entry</span>
          <span className="text-xs font-normal text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
            {selectedRepairJob?.item_description}
          </span>
        </div>
      }
      icon={Briefcase}
      maxWidth="max-w-xl"
      footer={
        <div className="flex justify-between w-full">
          <Button
            variant="ghost"
            className="gap-2 text-primary hover:text-primary hover:bg-primary/5"
            onClick={() => {
              onHide();
              handleViewLabor(
                repairJobs.find((job) => job.id === selectedRepairJob.id),
              );
            }}
          >
            <Users size={16} /> View All Labor
          </Button>

          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={onHide}
              disabled={addLaborMutation?.isPending}
            >
              Cancel
            </Button>
            <Button
              className="gap-2"
              onClick={handleValidateAndSave}
              disabled={isSaveDisabled}
            >
              {addLaborMutation?.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save size={16} /> Save Changes
                </>
              )}
            </Button>
          </div>
        </div>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 px-1">
        <div className="space-y-2">
          <Label className="font-medium">
            Technician <span className="text-destructive">*</span>
          </Label>
          <Select
            disabled={isTechniciansLoading}
            value={newLaborEntry.technician_id?.toString()}
            onValueChange={(val) =>
              handleLaborEntryChange({
                target: { name: "technician_id", value: val },
              })
            }
          >
            <SelectTrigger>
              <SelectValue
                placeholder={
                  isTechniciansLoading
                    ? "Loading technicians..."
                    : "Select Technician"
                }
              />
            </SelectTrigger>
            <SelectContent>
              {technicians.map((tech) => (
                <SelectItem key={tech.id} value={tech.id.toString()}>
                  {tech.full_name}
                  {tech.hourly_rate &&
                    ` - ${currencySymbol}${tech.hourly_rate}/hr`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label className="font-medium">
            Hourly Rate <span className="text-destructive">*</span>
          </Label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
              {currencySymbol}
            </span>
            <Input
              type="number"
              className="pl-7"
              name="hourly_rate"
              value={newLaborEntry.hourly_rate}
              onChange={handleLaborEntryChange}
              placeholder="0.00"
              min="0"
              max="9999.99"
              step="0.01"
              required
            />
          </div>
          <p className="text-[10px] text-muted-foreground italic">
            Auto-filled from technician profile, but editable
          </p>
        </div>

        {/* Start Time with Validation */}
        <div className="space-y-2">
          <Label className="font-medium">
            Start Time <span className="text-destructive">*</span>
          </Label>
          <Input
            type="datetime-local"
            name="start_time"
            value={newLaborEntry.start_time || ""}
            onChange={handleLaborEntryChange}
            className={cn(
              (isEndTimeWithoutStart || isStartInvalid) &&
              "border-destructive focus-visible:ring-destructive text-destructive",
            )}
            required
          />
          {isEndTimeWithoutStart ? (
            <p className="text-xs text-destructive font-medium flex items-center gap-1">
              <AlertCircle size={12} /> Start time is required when end time is set
            </p>
          ) : isStartInvalid ? (
            <p className="text-xs text-destructive font-medium flex items-center gap-1">
              <AlertCircle size={12} /> Invalid start date/time format
            </p>
          ) : null}
        </div>

        {/* End Time with Validation */}
        <div className="space-y-2">
          <Label className="font-medium">End Time</Label>
          <Input
            type="datetime-local"
            name="end_time"
            value={newLaborEntry.end_time || ""}
            min={newLaborEntry.start_time || undefined}
            onChange={handleLaborEntryChange}
            className={cn(
              (isEndBeforeStart || isEndInvalid) &&
              "border-destructive focus-visible:ring-destructive text-destructive",
            )}
          />
          {isEndBeforeStart ? (
            <p className="text-xs text-destructive font-medium flex items-center gap-1">
              <AlertCircle size={12} /> End time must be after start time
            </p>
          ) : isEndInvalid ? (
            <p className="text-xs text-destructive font-medium flex items-center gap-1">
              <AlertCircle size={12} /> Invalid end date/time format
            </p>
          ) : (
            <p className="text-[10px] text-muted-foreground italic">
              Leave empty if work is currently in progress
            </p>
          )}
        </div>

        <div className="md:col-span-2 space-y-2">
          <Label className="font-medium">Description</Label>
          <div className="rounded-md border border-input overflow-hidden min-h-[140px]">
            <DefaultEditor
              value={newLaborEntry.description || ""}
              onChange={(e) =>
                setNewLaborEntry({
                  ...newLaborEntry,
                  description: e,
                })
              }
              style={{
                height: "140px",
                border: "none",
                fontSize: "14px",
              }}
            />
          </div>
          <p className="text-[10px] text-muted-foreground text-right italic">
            {(newLaborEntry.description || "").length}/1000 characters
          </p>
        </div>

        {/* Validation Alerts and Calculation Summary */}
        {isEndBeforeStart && (
          <div className="md:col-span-2">
            <Alert
              variant="destructive"
              className="bg-destructive/10 border-destructive/20 text-destructive"
            >
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="text-xs font-medium">
                Invalid time range: End time must be after start time. Please adjust the times.
              </AlertDescription>
            </Alert>
          </div>
        )}

        {isEndTimeWithoutStart && (
          <div className="md:col-span-2">
            <Alert
              variant="destructive"
              className="bg-destructive/10 border-destructive/20 text-destructive"
            >
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="text-xs font-medium">
                Start time is required when an end time is specified.
              </AlertDescription>
            </Alert>
          </div>
        )}

        {hasValidStart && hasValidEnd && !isEndBeforeStart && newLaborEntry.hourly_rate && (
          <div className="md:col-span-2">
            <Alert className="bg-primary/5 border-primary/20">
              <Info className="h-4 w-4 text-primary" />
              <AlertDescription className="grid grid-cols-2 gap-4 mt-1">
                <div>
                  <span className="text-xs uppercase font-bold text-muted-foreground block">
                    Hours Worked
                  </span>
                  <span className="text-lg font-semibold">
                    {calculateHours()} hrs
                  </span>
                </div>
                <div>
                  <span className="text-xs uppercase font-bold text-muted-foreground block">
                    Total Cost
                  </span>
                  <span className="text-lg font-semibold text-primary">
                    {currencySymbol}
                    {calculateTotalCost()}
                  </span>
                </div>
              </AlertDescription>
            </Alert>
          </div>
        )}
      </div>
    </Modal>
  );
};

LaborEntriesModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  selectedRepairJob: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    item_description: PropTypes.string,
  }),
  newLaborEntry: PropTypes.shape({
    technician_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    hourly_rate: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    start_time: PropTypes.string,
    end_time: PropTypes.string,
    description: PropTypes.string,
  }).isRequired,
  setNewLaborEntry: PropTypes.func.isRequired,
  handleLaborEntryChange: PropTypes.func.isRequired,
  isTechniciansLoading: PropTypes.bool,
  technicians: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      full_name: PropTypes.string,
      hourly_rate: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }),
  ).isRequired,
  currencySymbol: PropTypes.string.isRequired,
  handleLaborEntrySave: PropTypes.func.isRequired,
  addLaborMutation: PropTypes.shape({
    isPending: PropTypes.bool,
  }),
  handleViewLabor: PropTypes.func.isRequired,
  repairJobs: PropTypes.array.isRequired,
};

export default LaborEntriesModal;
